/**
 * Pure filter/sort layer for the investor-book table (PT-5707).
 *
 * Kept free of React so the ticket's 500-row performance contract can be asserted directly
 * against these functions rather than through a mounted component. Both functions return new
 * arrays and never mutate their input.
 *
 * Neither function touches `share`: the server computes it against the node's full AUM, and the
 * AC forbids re-normalising it to a filtered subset.
 */
import {Investor} from './investorBook'

/** Columns the user can sort by. Email is deliberately not sortable — the AC lists four. */
export type SortKey = 'name' | 'funds' | 'share' | 'created'
export type SortDir = 'asc' | 'desc'

/** AC: "Default sort: Funds descending (largest investor first)". */
export const DEFAULT_SORT: {key: SortKey; dir: SortDir} = {key: 'funds', dir: 'desc'}

/** AC: the search input appears once the list exceeds 20 rows. */
export const SEARCH_THRESHOLD = 20

/**
 * Filters by investor name or email — case-insensitive, matching anywhere in the string.
 *
 * @param investors - The full investor book.
 * @param query - Raw user input; empty or whitespace-only returns the list unchanged.
 * @returns A new array of the matching investors, in the input's order.
 */
export const filterInvestors = (investors: Investor[], query: string): Investor[] => {
  const needle = query.trim().toLowerCase()
  if (!needle) return investors.slice()
  return investors.filter(
    (investor) =>
      investor.name.toLowerCase().includes(needle) || (investor.email?.toLowerCase().includes(needle) ?? false),
  )
}

/**
 * Compares two values of the same column, ordering ascending. `null` is not "small" — it means
 * "unknown", so it always sinks to the bottom regardless of direction (handled by the caller).
 */
const compare = (a: Investor, b: Investor, key: SortKey): number => {
  switch (key) {
    case 'name':
      return a.name.localeCompare(b.name)
    case 'funds':
      return a.funds - b.funds
    case 'share':
      return (a.share ?? 0) - (b.share ?? 0)
    case 'created':
      return (a.created?.getTime() ?? 0) - (b.created?.getTime() ?? 0)
    default:
      return 0
  }
}

/** True when the sort column has no value for this investor. */
const isMissing = (investor: Investor, key: SortKey): boolean =>
  (key === 'share' && investor.share == null) || (key === 'created' && investor.created == null)

/**
 * Sorts the book by a column, with `id` as a stable tiebreak so equal values never jitter.
 * Rows missing the sorted value are always last, in both directions.
 *
 * @param investors - The rows to sort (not mutated).
 * @param key - The column to sort by.
 * @param dir - Ascending or descending.
 * @returns A new, sorted array.
 */
export const sortInvestors = (investors: Investor[], key: SortKey, dir: SortDir): Investor[] => {
  const sign = dir === 'asc' ? 1 : -1
  return investors.slice().sort((a, b) => {
    const aMissing = isMissing(a, key)
    const bMissing = isMissing(b, key)
    if (aMissing !== bMissing) return aMissing ? 1 : -1
    const byColumn = aMissing && bMissing ? 0 : compare(a, b, key) * sign
    return byColumn || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  })
}
